import React, { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { CheckCircle, Calendar, Download, Mail, Clock, User, FileText, MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

const TestReceipt = () => {
    const contentRef = useRef(null);

    // Hardcoded dummy booking data for testing
    const booking = {
        user_name: "Test User",
        user_email: "test@example.com",
        phone: "9876543210",
        date: new Date().toISOString(),
        slot_time: new Date().toISOString(),
        duration: 3,
        guest_use_time: "10:00 AM - 01:00 PM",
        amount: 6500,
        deposit: 2500,
        total_amount: 10300
    };

    const txnid = "TEST_TXN_123456";

    const handlePrint = useReactToPrint({
        contentRef,
        documentTitle: `Receipt-${txnid}`,
    });

    const formatDuration = (d) => {
        if (!d) return '';
        const str = d.toString().toLowerCase();
        if (str.includes('hr')) return d.toString().replace(/hrhr/g, 'hr');
        return `${d}hr`;
    };

    return (
        <div className="min-h-screen bg-[#F4ECDE] py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h1 className="text-4xl font-bold text-[#471F21] mb-4">Test Receipt Page</h1>
                    <p className="text-xl text-[#5a3a3c]">This is a dummy page to test PDF download.</p>
                </div>

                <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8 border border-[#471F21]/10" ref={contentRef}>
                    <div className="bg-[#471F21] px-8 py-8 flex justify-between items-center">
                        <div className="text-[#F4ECDE]">
                            <h2 className="text-2xl font-bold font-serif">Booking Receipt</h2>
                            <p className="text-[#F4ECDE]/70 text-sm mt-1">Transaction ID: {txnid}</p>
                        </div>
                        {/* Logo might be missing if path is relative, using text if img fails */}
                        <div className="text-white font-bold text-xl">TTLY</div>
                    </div>

                    <div className="p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 border-b border-[#F4ECDE] pb-8">
                            <div>
                                <h3 className="text-xs font-bold text-[#471F21]/50 uppercase tracking-widest mb-4">Customer Details</h3>
                                <div className="space-y-4">
                                    <div className="flex items-center text-[#5a3a3c]">
                                        <User className="w-5 h-5 mr-3 text-[#471F21]/40" />
                                        <span className="font-semibold">{booking.user_name}</span>
                                    </div>
                                    <div className="flex items-center text-[#5a3a3c]">
                                        <Mail className="w-5 h-5 mr-3 text-[#471F21]/40" />
                                        <span>{booking.user_email}</span>
                                    </div>
                                    <div className="flex items-center text-[#5a3a3c]">
                                        <Phone className="w-5 h-5 mr-3 text-[#471F21]/40" />
                                        <span>{booking.phone}</span>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-xs font-bold text-[#471F21]/50 uppercase tracking-widest mb-4">Booking Details</h3>
                                <div className="space-y-4">
                                    <div className="flex items-center text-[#5a3a3c]">
                                        <Calendar className="w-5 h-5 mr-3 text-[#471F21]/40" />
                                        <span className="font-semibold">{new Date(booking.date).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center text-[#5a3a3c]">
                                        <Clock className="w-5 h-5 mr-3 text-[#471F21]/40" />
                                        <span>{booking.guest_use_time} ({formatDuration(booking.duration)})</span>
                                    </div>
                                    <div className="flex items-center text-[#5a3a3c]">
                                        <MapPin className="w-5 h-5 mr-3 text-[#471F21]/40" />
                                        <span>Thaathayya Illu</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-[#F4ECDE]/30 rounded-2xl p-6 mb-8 border border-[#471F21]/5">
                            <h3 className="text-xs font-bold text-[#471F21]/50 uppercase tracking-widest mb-4">Payment Summary</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-[#5a3a3c]">Session Price</span>
                                    <span className="text-[#471F21] font-bold">₹{booking.amount.toLocaleString('en-IN')}</span>
                                </div>
                                {booking.deposit > 0 && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-[#5a3a3c]">Deposit</span>
                                        <span className="text-[#471F21] font-bold">₹{booking.deposit.toLocaleString('en-IN')}</span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center text-xl font-bold pt-4 border-t border-[#471F21]/10 mt-2">
                                    <span className="text-[#471F21]">Total Paid</span>
                                    <span className="text-[#471F21]">₹{booking.total_amount.toLocaleString('en-IN')}</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white border-2 border-[#471F21]/10 rounded-2xl p-6">
                            <h3 className="font-bold text-[#471F21] mb-4 flex items-center">
                                <FileText className="w-5 h-5 mr-2" />
                                Important Information
                            </h3>
                            <p className="text-sm text-[#5a3a3c]">This is a test receipt rendering.</p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-center">
                    <button
                        onClick={() => handlePrint()}
                        className="bg-[#471F21] text-white px-8 py-4 rounded-2xl font-bold flex items-center justify-center hover:bg-[#5a3a3c] transition-all shadow-lg hover:shadow-xl"
                    >
                        <Download className="w-5 h-5 mr-2" />
                        Download Test Receipt
                    </button>
                </div>
                <div className="text-center mt-8">
                    <Link to="/" className="text-[#471F21] underline">Back to Home</Link>
                </div>
            </div>
            <style dangerouslySetInnerHTML={{
                __html: `
        @media print {
          body { background: white !important; }
          .min-h-screen { background: white !important; padding: 0 !important; }
          button, a, .text-center.mb-12 { display: none !important; }
          .max-w-3xl { max-width: 100% !important; margin: 0 !important; }
          .bg-white { border: none !important; shadow: none !important; }
          .rounded-3xl { border-radius: 0 !important; }
        }
      `}} />
        </div>
    );
};

export default TestReceipt;
